'use client';

import { useIdentityContext } from '@cc-shopping/platform-analytics/IdentityContext';
import { ApprovalOddsErrorCategory } from '@cc-shopping/utils/errors/approvalOddsErrors';
import { datadogRum } from '@datadog/browser-rum';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { APPROVAL_ODDS_API_PATH } from 'src/app/api/credit-cards/og/approval-odds/config';
import type { ApprovalOddsResponse } from './constants';
type ApprovalLikelihoodContextValue = {
  isLoading: boolean;
  approvalLikelihoods: ApprovalOddsResponse;
};
const ApprovalLikelihoodContext = createContext<ApprovalLikelihoodContextValue>({
  isLoading: false,
  approvalLikelihoods: {}
});
export function ApprovalLikelihoodProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isLoggedIn
  } = useIdentityContext();
  const [isLoading, setIsLoading] = useState(false);
  const [approvalLikelihoods, setApprovalLikelihoods] = useState<ApprovalOddsResponse>({});
  useEffect(() => {
    if (isLoggedIn) {
      const fetchApprovalLikelihoods = async () => {
        try {
          setIsLoading(true);
          datadogRum.addAction('Approval odds fetch started', {
            approvalOddsUrl: APPROVAL_ODDS_API_PATH
          });
          const rawData = await fetch(APPROVAL_ODDS_API_PATH, {
            credentials: 'include'
          });
          if (!rawData.ok) {
            throw new Error(`Approval odds API returned status: ${rawData.status}`);
          }
          if (rawData.status === 204) {
            // No content is an expected response for users without a TU report.
            setApprovalLikelihoods({});
            return;
          }
          const response = (await rawData.json()) as ApprovalOddsResponse;
          setApprovalLikelihoods(response);
        } catch (err) {
          const error = new Error('Failed to fetch approval odds data', {
            cause: err
          });
          datadogRum.addError(error, {
            approvalOddsUrl: APPROVAL_ODDS_API_PATH,
            isLoggedIn: String(isLoggedIn),
            errorCategory: ApprovalOddsErrorCategory.FETCH_ERROR
          });
        } finally {
          setIsLoading(false);
        }
      };
      void fetchApprovalLikelihoods();
    }
  }, [isLoggedIn]);
  const value = useMemo(() => ({
    isLoading,
    approvalLikelihoods
  }), [isLoading, approvalLikelihoods]);
  return <ApprovalLikelihoodContext.Provider value={value}>
      {children}
    </ApprovalLikelihoodContext.Provider>;
}
export const useApprovalLikelihoodContext = () => {
  const context = useContext(ApprovalLikelihoodContext);
  if (!context) {
    console.warn("useApprovalLikelihoodContext must be used within an ApprovalLikelihoodProvider");
  }
  return context;
};